import { resolveSiteReference } from './siteIdentifier'

export interface UserIdentityInput {
  userPrincipalName?: string | null
  mail?: string | null
}

export function isSwissCountry(country?: string | null): boolean {
  return ['switzerland', 'ch', 'che', 'schweiz', 'suisse', 'svizzera', 'svizra']
    .includes(country?.trim().toLowerCase() ?? '')
}

interface ReportSiteInput {
  id?: string | null
  siteNumber?: string | null
  country?: string | null
}

export function reportContainsSwissSite(
  primarySiteCountry: string | undefined | null,
  accountSites: readonly ReportSiteInput[],
  linkedSiteIds: readonly (string | undefined | null)[],
): boolean {
  if (isSwissCountry(primarySiteCountry))
    return true

  const linkedIds = new Set(linkedSiteIds.filter((id): id is string => Boolean(id)))
  return accountSites.some(
    site =>
      [...linkedIds].some(id => resolveSiteReference(accountSites, id) === site)
      && isSwissCountry(site.country),
  )
}

export function isProtectasEmail(email?: string | null): boolean {
  if (!email)
    return false

  const parts = email.trim().toLowerCase().split('@')
  if (parts.length !== 2 || !parts[0])
    return false

  return parts[1] === 'protectas.com' || parts[1] === 'protectas.ch'
}

export function isProtectasUserIdentity(
  user?: UserIdentityInput | null,
): boolean {
  if (!user)
    return false
  return isProtectasEmail(user.userPrincipalName) || isProtectasEmail(user.mail)
}

export function replaceProtectasBrand(text?: string): string | undefined {
  return text ? text.replace(/Securitas/g, 'Protectas') : text
}

/**
 * Builds the vue-i18n post-translation handler that rebrands UI copy.
 *
 * Installed from `plugins/02.translation.ts` rather than declared in
 * `i18n/i18n.config.ts`, so it closes over store refs that are already live
 * instead of resolving Pinia on every translation call.
 */
export function createBrandPostTranslationHandler<T>(
  shouldSubstitute: () => boolean,
): (translated: T) => T {
  return (translated: T) =>
    typeof translated === 'string' && shouldSubstitute()
      ? ((replaceProtectasBrand(translated) ?? translated) as T)
      : translated
}

export type ReportBrandQueryValue = string | null | (string | null)[] | undefined

/**
 * Resolves the brand for the current view.
 *
 * An explicit report brand — provided by published Web Report metadata or a PDF
 * render query — is authoritative for that report render. This keeps the same
 * report consistently branded regardless of who views it.
 *
 * When no explicit report brand is available, the authenticated viewer's
 * identity determines the application brand.
 */
export function resolveProtectasBrand(
  reportProtectasBrand: boolean | null | undefined,
  isProtectasUser: boolean,
): boolean {
  return reportProtectasBrand ?? isProtectasUser
}

/**
 * Reads the explicit report brand off a PDF render link, preferring
 * `protectasBrand` over the legacy `protectasUser`.
 *
 * The two parameters resolve identically but do not mean the same thing, and
 * conflating them invites confusion later: `protectasBrand` states the brand of
 * the report itself, whereas `protectasUser` serialized the identity of the
 * user who generated the link so the separate PDF renderer could reproduce
 * their branding. Links carrying the legacy name are still in circulation.
 *
 * Returns null when the link states no brand, which leaves the viewer's
 * identity to decide.
 */
export function parseReportBrandQuery(
  query: Record<string, ReportBrandQueryValue>,
): boolean | null {
  const raw = query.protectasBrand ?? query.protectasUser
  const value = Array.isArray(raw) ? raw[raw.length - 1] : raw
  if (value === undefined || value === null)
    return null
  return value === 'true'
}

export function isDefaultSecuritasUrl(url?: string | null): boolean {
  if (!url?.trim())
    return false
  const normalized = url
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/+$/, '')
  return normalized === 'securitas.com'
}

export function isProtectasSiteUrl(url?: string | null): boolean {
  const value = url?.trim()
  if (!value)
    return false

  try {
    const parsed = new URL(
      /^https?:\/\//i.test(value) ? value : `https://${value}`,
    )
    const hostname = parsed.hostname.toLowerCase().replace(/^www\./, '')
    return hostname === 'protectas.com' || hostname === 'protectas.ch'
  }
  catch {
    return false
  }
}

export function formatAppTitle(
  title?: string | null,
  isProtectas: boolean = false,
): string {
  const brand = isProtectas ? 'Protectas Advisor' : 'Securitas Advisor'
  return title?.trim() ? `${brand} | ${title.trim()}` : brand
}

export function resolveLearnMoreHref(
  savedUrl: string | undefined | null,
  isProtectas: boolean,
): string {
  const value = savedUrl?.trim() ?? ''
  if (isProtectas && (!value || isDefaultSecuritasUrl(value)))
    return 'https://www.protectas.com/en-ch/'
  if (!value)
    return 'https://www.securitas.com/'
  return /^https?:\/\//i.test(value) ? value : `https://${value}`
}
